import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';

import type { RdwVehicleCardItem } from './formly-fields/rdw.types';

/** Up to this many rows the card shows everything; above it, it collapses to `COLLAPSED_COUNT`. */
const COLLAPSE_ABOVE = 8;
/** Rows shown while collapsed: three rows of two. */
const COLLAPSED_COUNT = 6;

let nextCardId = 0;

/**
 * The vehicle facts the RDW lookup returned (#21461): the navy "surface/custom" info card from
 * Figma, as a two-column definition list with the label above its value.
 *
 * Two columns when the card is wide enough, one when it is not. The grid reacts to the card's own
 * width (auto-fit), not to the viewport, because the create form can sit in a narrow column on a
 * wide screen; one column at narrow widths is also what WCAG 1.4.10 (reflow) asks for. The list is
 * a real `<dl>` in row order, so a screen reader reads label, value, label, value.
 *
 * Above `COLLAPSE_ABOVE` rows the card starts collapsed and a disclosure button names how many
 * facts there are ("Toon alle 12 voertuiggegevens"). The button carries `aria-expanded` and
 * `aria-controls` and keeps focus, so expanding never moves the reader. The rows arrive already
 * filtered and in reading order (`rdwPreviewRank`), so what stays visible when collapsed is what
 * identifies the vehicle.
 *
 * Presentational: rows in, nothing out.
 */
@Component({
  selector: 'dd-rdw-vehicle-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    :host {
      display: block;
    }

    .dd-rdw-vehicle-card__list {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(max(12rem, calc(50% - 1rem)), 1fr));
      column-gap: 1.5rem;
      row-gap: 0.75rem;
      margin: 0;
    }

    .dd-rdw-vehicle-card__list dd {
      margin: 0;
    }

    .dd-rdw-dot {
      display: inline-block;
      width: 8px;
      height: 8px;
      flex: none;
      border-radius: 9999px;
      background: #106bd3;
    }
  `,
  template: `
    <div class="mb-4 flex flex-col gap-0.5">
      <div class="flex flex-col gap-3 rounded-lg bg-[#f6faff] p-4 dark:bg-[#030833]">
        <dl class="dd-rdw-vehicle-card__list" [id]="listId" data-cy="rdw-vehicle-card">
          @for (item of visibleItems(); track $index) {
            <div class="flex min-w-0 flex-col gap-0.5 text-sm leading-[1.4]" data-cy="rdw-vehicle-card-item">
              <!-- Figma dark: label = foreground/secondary #C7C7CC, value = white. text-accent
                   renders too dark on the custom navy card, so pin the dark colors. -->
              <dt class="text-xs font-medium text-accent dark:text-[#C7C7CC]">{{ item.label }}</dt>
              <dd class="break-words font-medium dark:text-white">{{ item.display }}</dd>
            </div>
          }
        </dl>
        @if (collapsible()) {
          <button
            type="button"
            class="self-start text-sm font-medium underline underline-offset-2 dark:text-white"
            [attr.aria-expanded]="expanded()"
            [attr.aria-controls]="listId"
            (click)="expanded.set(!expanded())"
            data-cy="rdw-vehicle-card-toggle">
            {{ expanded() ? 'Toon minder' : 'Toon alle ' + items().length + ' voertuiggegevens' }}
          </button>
        }
      </div>
      <div class="flex items-center gap-1 py-1 text-xs font-medium text-accent dark:text-[#C7C7CC]">
        <span class="dd-rdw-dot"></span>
        Gegevens opgehaald via RDW
      </div>
    </div>
  `,
})
export class RdwVehicleCardComponent {
  readonly items = input<readonly RdwVehicleCardItem[]>([]);

  protected readonly listId = `dd-rdw-vehicle-card-${nextCardId++}`;
  protected readonly expanded = signal(false);
  protected readonly collapsible = computed(() => this.items().length > COLLAPSE_ABOVE);
  protected readonly visibleItems = computed(() =>
    this.collapsible() && !this.expanded() ? this.items().slice(0, COLLAPSED_COUNT) : this.items()
  );
}
